/*
 * Copyright 2026 North Carolina State University
 */

import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import { formatDateTime } from '../../../utils/timeFormat';

const ACTION_LABELS = {
  comment: 'Admin Comment',
  admin_edit: 'Edited by Admin',
  // Pre-rename actions, still present on older requests.
  approve: 'Completed',
  reject: 'Rejected',
  revision_requested: 'Revision Requested',
  reopen: 'Reopened'
};

const ACTION_COLORS = {
  comment: 'default',
  admin_edit: 'warning',
  approve: 'success',
  reject: 'error',
  revision_requested: 'info',
  reopen: 'primary'
};

const STATUS_COLORS = {
  New: 'info',
  Pending: 'warning',
  Completed: 'success',
  Rejected: 'error'
};

const personName = (person, fallback) => {
  if (!person || typeof person === 'string') return fallback;
  return `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email || fallback;
};

const RequestHistoryTimeline = ({ request, isAdmin = false }) => {
  if (!request) return null;

  const events = [];

  events.push({
    type: 'submitted',
    label: 'Submitted',
    color: 'primary',
    actor: request.submitted_by
      ? `${request.submitted_by.first_name || ''} ${request.submitted_by.last_name || ''}`.trim() || request.submitted_by.email
      : '—',
    at: request.submitted_at,
    detail: null
  });

  (request.revisions || []).forEach((rev) => {
    events.push({
      type: 'revised',
      label: 'PI Revised & Resubmitted (legacy)',
      color: 'info',
      actor: rev.revised_by
        ? `${rev.revised_by.first_name || ''} ${rev.revised_by.last_name || ''}`.trim() || rev.revised_by.email
        : 'PI',
      at: rev.revised_at,
      detail: rev.comment || null
    });
  });

  // Who moved the request to which status, and when. Moves are always manual.
  (request.status_history || []).forEach((move) => {
    // The initial 'New' entry is the submission, already listed above.
    if (!move.from_status && move.to_status === 'New') return;
    events.push({
      type: 'status_change',
      label: `Moved to ${move.to_status}`,
      color: STATUS_COLORS[move.to_status] || 'default',
      actor: personName(move.changed_by, move.changed_by_name || 'Admin'),
      at: move.changed_at,
      detail: move.comment || null
    });
  });

  // Field-level form edits by either side — what replaced the revision cycle.
  (request.form_changes || []).forEach((change) => {
    const fields = (change.changes || []).map(c => c.label || c.field);
    events.push({
      type: 'form_change',
      label: change.changed_by_role === 'admin' ? 'Form edited by Admin' : 'Form edited by Client',
      color: change.changed_by_role === 'admin' ? 'warning' : 'primary',
      actor: personName(change.changed_by, change.changed_by_name || '—'),
      at: change.changed_at,
      detail: change.comment || null,
      editedFields: fields
    });
  });

  // Requests edited before form_changes existed only have an 'admin_edit'
  // response to show, so those are kept. One written alongside a form_changes
  // entry describes the same edit with less detail, and is dropped.
  const formChangeTimes = (request.form_changes || [])
    .map(c => new Date(c.changed_at || 0).getTime());
  const hasMatchingFormChange = (at) => {
    const t = new Date(at || 0).getTime();
    return formChangeTimes.some(ft => Math.abs(ft - t) < 5000);
  };

  (request.admin_responses || []).forEach((resp) => {
    // A status move is already listed from status_history; listing the paired
    // admin_responses row too would double every move in the timeline.
    if (resp.action_type === 'status_change') return;
    const editedFields = resp.edited_fields || [];
    if (resp.action_type === 'admin_edit'
      && editedFields.every(f => f === 'form_data')
      && hasMatchingFormChange(resp.responded_at)) {
      return;
    }
    events.push({
      type: resp.action_type,
      label: ACTION_LABELS[resp.action_type] || resp.action_type,
      color: ACTION_COLORS[resp.action_type] || 'default',
      actor: resp.responded_by
        ? `${resp.responded_by.first_name || ''} ${resp.responded_by.last_name || ''}`.trim() || resp.responded_by.email
        : 'Admin',
      at: resp.responded_at,
      detail: resp.comment || null,
      attachments: resp.attachments || [],
      // 'form_data' is the schema's field name, not something an admin recognises.
      editedFields: editedFields.map(f => (f === 'form_data' ? 'the request form' : f))
    });
  });

  if (request.is_deleted) {
    events.push({
      type: 'deleted',
      label: 'Deleted',
      color: 'error',
      actor: request.deleted_by
        ? `${request.deleted_by.first_name || ''} ${request.deleted_by.last_name || ''}`.trim() || request.deleted_by.email
        : 'Admin',
      at: request.deleted_at,
      detail: null
    });
  }

  events.sort((a, b) => new Date(a.at) - new Date(b.at));

  if (events.length === 0) return null;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Activity & Audit
      </Typography>
      <Stack spacing={2}>
        {events.map((ev, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ minWidth: 12, mt: 0.6 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: `${ev.color}.main` }} />
              {i < events.length - 1 && (
                <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mx: 'auto', minHeight: 32 }} />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={ev.label} color={ev.color} variant="outlined" />
                <Typography variant="body2" color="text.secondary">
                  {ev.actor} · {formatDateTime(ev.at)}
                </Typography>
              </Stack>
              {ev.detail && (
                <Typography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  {ev.detail}
                </Typography>
              )}
              {ev.editedFields?.length > 0 && (ev.type === 'form_change' || isAdmin) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Fields: {ev.editedFields.join(', ')}
                </Typography>
              )}
              {ev.attachments?.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {ev.attachments.length} attachment{ev.attachments.length !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
      {request.is_deleted && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="error">This request is deleted.</Typography>
        </>
      )}
    </Paper>
  );
};

export default RequestHistoryTimeline;
